import TextFieldComponent from "./TextFieldComponent";
import {IconButton} from "@mui/material";
import React from "react";
import RadiosComponent from "./RadiosComponent";
import CheckboxesComponent from "./CheckboxesComponent";
import NumberComponent from "./NumberComponent";
import DropdownComponent from "./DropdownComponent";
import DateComponent from "./DateComponent";
import {useTranslation} from "i18nano";
import {StatusUpdateAnswer} from "../types/UpdatePartyObject";
import {AppAnswer, AppAttachment, AppQuestion} from "../types/types";
import FileComponent from "./FileComponent";
import {v4} from 'uuid';
import DeleteIcon from '@mui/icons-material/Delete';
import useNetwork from "../hooks/useNetwork";
import {useDocumentViewer} from "../hooks/useDocumentViewer";
import MultipleInputsComponent from "./MultipleInputsComponent";
import {CommonProps} from "./types/types";
import PopupConfirm from "./PopupConfirm";

interface CommonComponentProps {
    item: AppQuestion,
    groupOrder?: number,
    groupId?: number,
    onInputChange: (value: string, item: AppQuestion, answerId: string, answerOrder: number, groupId?: number, groupOrder?: number, debounced?: boolean) => void,
    onAddNewTextField: (itemId: number, groupId?: number, groupOrder?: number,) => void,
    onClickRemoveTextField: (itemId: number, answerId: string, groupId?: number, groupOrder?: number) => void,
    onFilesUpload: (e: FileList | null, item: AppQuestion, valueId: string, groupId?: number, order?: number) => void,
    onFileListChange: (result: AppAttachment[], item: AppQuestion, valueId: string, groupId?: number, order?: number) => void
}

const getQuestionCoords = (item: AppQuestion) => {
    const firstAnswer = item?.answers?.[0];
    return getAnswerCoords(firstAnswer);
};
export const getAnswerCoords = (item?: AppAnswer) => {
    return {
        answerId: item?.id || v4(),
        answerOrder: item?.order ?? 0,
        status: item?.status,
        errorText: item?.errorText,
        text: item?.text ?? ""
    };
};

const CommonComponent = ({
                             item,
                             groupOrder,
                             groupId,
                             onAddNewTextField,
                             onClickRemoveTextField,
                             onFilesUpload,
                             onFileListChange,
                             onInputChange
                         }: CommonComponentProps) => {
    const t = useTranslation();
    const {online} = useNetwork();
    const {viewDocument} = useDocumentViewer();

    const handleInputChange = (value: string, answerId: string, answerOrder: number, debounced: boolean = false) => {
        onInputChange(value, item, answerId, answerOrder, groupId, groupOrder, debounced)
    }

    const onFocus = (e: React.FocusEvent<any> | React.ChangeEvent<any>,
                     answerId: string, answerOrder: number, status?: StatusUpdateAnswer) => {
        if (online && status === StatusUpdateAnswer.ERROR) {
            handleInputChange(e.target.value, answerId, answerOrder, false);
        }
    }

    if (!item || item.hidden) return null;

    const id = groupId ? `${groupId}-${groupOrder}-${item.id}` : `${item.id}`;
    const {answerId, answerOrder, status, errorText, text} = getQuestionCoords(item);

    const baseProps: CommonProps = {
        id,
        displayText: item.displayText,
        required: item.required,
        editable: item.editable,
        hint: item.hint,
        status,
        errorText,
        value: text
    };


    if (item.questionType === 1) {
        if (!item.allowMultiple) {
            return (
                    <TextFieldComponent
                            {...baseProps}
                            onChange={e => handleInputChange(e.target.value, answerId, answerOrder, true)}
                            onFocus={(e) => onFocus(e, answerId, answerOrder, status)}
                    />
            )
        } else {
            return <MultipleInputsComponent item={item}
                                            onInputChange={onInputChange}
                                            groupId={groupId}
                                            onAddNewTextField={onAddNewTextField}
                                            onClickRemoveTextField={onClickRemoveTextField}
                                            groupOrder={groupOrder}
                                            id={id}
                                            type="text"
                                            onFocus={onFocus}/>;
        }
    }
    if (item.questionType === 2) {
        return (
                <RadiosComponent
                        {...baseProps}
                        onChange={e => {
                            let value = e.target.value;
                            const debounced = e.target.value.startsWith("o:");
                            if (e.target.value === "Other") {
                                value = "o:";
                            }
                            handleInputChange(value, answerId, answerOrder, debounced);
                        }
                        }
                        onFocus={e => onFocus(e, answerId, answerOrder, status)}
                        choices={item.choices}
                        other={item.other}
                        radioGroupName={`${item.id}`}
                />
        )
    }
    if (item.questionType === 3) {
        return (
                <CheckboxesComponent
                        {...baseProps}
                        onChange={e => {
                            handleInputChange(e.target.value, answerId, answerOrder, e.target.formNoValidate);
                        }}
                        onFocus={e => onFocus(e, answerId, answerOrder, status)}
                        choices={item.choices}
                        other={item.other}
                />
        )
    }
    if (item.questionType === 4) {
        if (!item.allowMultiple) {
            return (
                    <NumberComponent
                            {...baseProps}
                            errorText={item?.answers[0]?.errorText}
                            onChange={e => {
                                handleInputChange(e.target.value, answerId, answerOrder, true)
                            }}
                            onFocus={e => onFocus(e, answerId, answerOrder, status)}
                    />
            )
        } else {
            return <MultipleInputsComponent item={item}
                                            onInputChange={onInputChange}
                                            groupId={groupId}
                                            onAddNewTextField={onAddNewTextField}
                                            onClickRemoveTextField={onClickRemoveTextField}
                                            groupOrder={groupOrder}
                                            id={id}
                                            type="number"
                                            onFocus={onFocus}/>;
        }
    }
    if (item.questionType === 5) {
        return (
                <DropdownComponent
                        {...baseProps}
                        radioGroupName={`${item.id}`}
                        choices={item.choices}
                        other={item.other}
                        onChange={e => {
                            let value = `${e.target.value}`;
                            const debounced = value.startsWith("o:");
                            if (value === "Other") {
                                value = "o:";
                            }
                            handleInputChange(value, answerId, answerOrder, debounced);
                        }}
                        onFocus={e => onFocus(e, answerId, answerOrder, status)}
                />
        )
    }
    if (item.questionType === 6) {
        return (
                <FileComponent {...baseProps}
                               onFileClick={async a => await viewDocument(a.id)}
                               attachments={item.answers[0]?.attachments}
                               onReorder={a => {
                                   onFileListChange(a, item, item.answers[0]?.id || v4(), groupId, groupOrder);
                               }}
                               onFilesUpload={a => {
                                   onFilesUpload(a, item, item.answers[0]?.id || v4(), groupId, groupOrder);
                               }}
                               onClickDelete={a => {
                                   onFileListChange(item.answers[0].attachments.filter(p => p.id !== a.id), item, item.answers[0]?.id || v4(), groupId, groupOrder);
                               }}


                />
        )
    }
    if (item.questionType === 7) {
        return (
                <DateComponent
                        {...baseProps}
                        onChange={date => {
                            if (!date) {
                                handleInputChange("", answerId, answerOrder, true);
                                return;
                            }

                            // 2. Приводимо все до об'єкта Date, незалежно від того, прийшов рядок чи Date
                            // Якщо прийшов об'єкт Date, Date.parse(date) або new Date(date) спрацює коректно
                            const dateObj = typeof date === "string" ? new Date(Date.parse(date)) : date;

                            // 3. Перевіряємо на "Invalid Date"
                            if (isNaN(dateObj.getTime())) {
                                handleInputChange("DateNoValidSecret", answerId, answerOrder, true);
                                return;
                            }

                            // 4. Безпечно дістаємо компоненти дати
                            const year = dateObj.getFullYear();
                            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                            const day = String(dateObj.getDate()).padStart(2, '0');

                            const dateToServer = `${year}-${month}-${day}T00:00:00.000`;

                            handleInputChange(dateToServer, answerId, answerOrder, true);
                        }}
                        onFocus={e => onFocus(e, answerId, answerOrder, status)}
                />
        )
    }
    if (item.questionType === 8) {
        return <MultipleInputsComponent item={item}
                                        onInputChange={onInputChange}
                                        groupId={groupId}
                                        onAddNewTextField={onAddNewTextField}
                                        onClickRemoveTextField={onClickRemoveTextField}
                                        groupOrder={groupOrder}
                                        id={id}
                                        type="text"
                                        onFocus={onFocus}
                                        endAdornment={(id) => {
                                            return <PopupConfirm
                                                    text={t("Do you really want to delete this party")}
                                                    onYes={() => onClickRemoveTextField(item.id, id, groupOrder, groupId)}
                                            >
                                                <IconButton
                                                        onMouseDown={e => e.preventDefault()}
                                                        edge="end"
                                                        disabled={!item.editable}
                                                        tabIndex={-1}
                                                >
                                                    <DeleteIcon/>
                                                </IconButton>
                                            </PopupConfirm>
                                        }}/>
    }
    if (item.questionType === 9) {
        return (
                <DropdownComponent
                        {...baseProps}
                        radioGroupName={`${item.id}`}
                        choices={item.choices}
                        other={item.other}
                        onChange={e => handleInputChange(e.target.value, answerId, answerOrder, false)}
                        onFocus={e => onFocus(e, answerId, answerOrder, status)}
                />
        )
    }
    return null;
}


export default CommonComponent;